import { gql } from 'shared/__generated__';
import { SlimNotificationItemFragment } from 'shared/__generated__/graphql';
import SidebarFeedItem from '../feed/sidebar-feed-item';
import { useCallback } from 'react';
import { Tooltip } from '@chakra-ui/react';
import { InView } from 'react-intersection-observer';
import { useApolloClient, useMutation } from '@apollo/client';
import { getRelativeDateString } from 'shared/dates';
import { useAuth } from 'shared/misc/hooks/useAuth';
import _ from 'lodash';
import { Link as TWLink } from '@tanstack/react-router';
import { useThemeColors } from 'shared/misc/hooks/useThemeColors';
import { useIsBackgroundLight } from 'shared/misc/hooks/useContrastHighlight';
import { StyledStar } from 'web2/app/routes/u/$username/-profile-stars';

gql(/* GraphQL */ `
  fragment SlimNotificationItem on GenericNotification {
    id
    title
    subtitle
    body
    path
    type
    createdAt
    seen
    targetFeedItem {
      ...NotificationFeedItem
    }
  }
`);

export default function SlimNotificationItem({
  notification,
  isInTicker = false,
}: {
  notification: SlimNotificationItemFragment;
  isInTicker?: boolean;
}) {
  if (notification.targetFeedItem) {
    return (
      <SidebarFeedItem
        key={notification.targetFeedItem.id}
        feedItem={notification.targetFeedItem}
        showHasSeenDot
        truncateLength={45}
        highlightText
        isInTicker={isInTicker}
      />
    );
  }

  return (
    <SlimGenericNotificationItem
      key={notification.id}
      notification={notification}
      showHasSeenDot
      truncateLength={45}
      highlightText
      isInTicker={isInTicker}
    />
  );
}

interface SlimGenericNotificationItemProps {
  notification: SlimNotificationItemFragment;
  markSeen?: boolean;
  showHasSeenDot?: boolean;
  size?: 'small' | 'large';
  truncateLength?: number;
  highlightText?: boolean;
  withVerticalSpacing?: boolean;
  isInTicker?: boolean;
}

function SlimGenericNotificationItem({
  notification,
  markSeen = false,
  showHasSeenDot,
  size = 'small',
  truncateLength = 25,
  highlightText,
  withVerticalSpacing = false,
  isInTicker = false,
}: SlimGenericNotificationItemProps) {
  const auth = useAuth<true>();

  const client = useApolloClient();

  const [markAsSeenFn] = useMutation(
    gql(/* GraphQL */ `
      mutation MarkGenericNotificationAsSeen($targetGenericNotificationId: String!) {
        markGenericNotificationAsSeen(targetGenericNotificationId: $targetGenericNotificationId) {
          id
          seen
        }
      }
    `),
    {
      refetchQueries: ['getNotificationsView'],
    },
  );

  const markAsSeen = useCallback(() => {
    markAsSeenFn({
      variables: {
        targetGenericNotificationId: notification.id,
      },
      refetchQueries: ['getNotificationsView'],
      optimisticResponse: {
        __typename: 'Mutation',
        markGenericNotificationAsSeen: {
          __typename: 'GenericNotification',
          id: notification.id,
          seen: true,
        },
      },
    });
  }, [client, markAsSeenFn, notification]);

  if (!notification.title) {
    return null;
  }

  const imageDimension = size === 'small' ? 24 : 32;

  const themeColors = useThemeColors();

  const isLight = useIsBackgroundLight(themeColors.background);

  const originUserFallbackLogoColor = !isLight ? 'white' : 'blue';

  const fallbackAvatarSrc = `https://files.pi.fyi/default-profile-photo-${
    originUserFallbackLogoColor || 'blue'
  }-small.png`;

  let imageNode = (
    <StyledStar width={imageDimension} height={imageDimension} staticSize fill="var(--theme-menu-text)" />
  );

  const finalImageNode = imageNode;
  // const finalImageNode = null;

  // const finalImageNode =
  //   imageNode && (imageLink ? <TWLink to={imageLink}>{imageNode}</TWLink> : imageNode);

  const createContentNode = (isMultiLine = false) => {
    if (!notification.path || !notification.title) return null;

    return (
      <TWLink to={notification.path} className={'typography-action-sm text-theme-main'}>
        <div
          className={`overflow-hidden ${
            isMultiLine
              ? 'whitespace-normal line-clamp-2'
              : 'whitespace-nowrap text-ellipsis no-of-lines-1'
          } typography-action-sm h-fit mr-[32px] ${
            highlightText ? 'text-theme-menu-text hover:text-theme-menu-highlight' : 'text-theme-main'
          }`}
        >
          {isMultiLine
            ? notification.title
            : _.truncate(notification.title, { length: truncateLength })}
        </div>
      </TWLink>
    );
  };

  const finalContentNode = createContentNode(false);
  const multiLineContentNode = createContentNode(true);

  if (
    (!finalContentNode && isInTicker) ||
    (!multiLineContentNode && !isInTicker) ||
    !finalImageNode
  ) {
    return null;
  }

  const singleLineLayout = (
    <div
      className={`flex items-center w-full relative overflow-x-visible ${
        withVerticalSpacing ? 'mb-[6px]' : ''
      }`}
      onClick={() => {
        if ('seen' in notification && !notification.seen) {
          markAsSeen();
        }
      }}
    >
      <div className="mr-[10px] relative">
        {finalImageNode}
        {showHasSeenDot && 'seen' in notification && !notification.seen && (
          <>
            <div className="absolute text-brand-highlight text-[50px] top-[-1px] right-[-1px] w-[5px] h-[5px] bg-brand-highlight rounded-[2.5px] dot" />
          </>
        )}
      </div>
      <Tooltip
        openDelay={1000}
        label={
          <div className="p-[6px]">
            <div>
              {notification.createdAt
                ? getRelativeDateString(new Date(notification.createdAt))
                : ''}
            </div>
          </div>
        }
      >
        <div className="space-y-[2px]">
          {finalContentNode}
          {notification.createdAt && size === 'large' ? (
            <div className="typography-action-sm text-[11px] opacity-40">
              {getRelativeDateString(new Date(notification.createdAt))}
            </div>
          ) : (
            ''
          )}
        </div>
      </Tooltip>
    </div>
  );

  const WrapperElement = notification.path ? TWLink : 'div';
  const WrapperElementProps = notification.path ? { to: notification.path } : {};

  const multiLineLayout = (
    <WrapperElement
      className="flex items-center w-full mb-[12px] text-theme-menu-text hover:text-theme-menu-highlight"
      {...WrapperElementProps}
    >
      <div className="mr-2">{finalImageNode}</div>
      <div
        className={`w-full hover:!text-theme-menu-highlight relative ${
          withVerticalSpacing ? 'mb-[16px]' : ''
        }`}
        onClick={() => {
          if ('seen' in notification && !notification.seen) {
            markAsSeen();
          }
        }}
      >
        <div className="flex relative">
          {showHasSeenDot && 'seen' in notification && !notification.seen && (
            <div className="absolute text-brand-highlight text-[50px] top-[-1px] left-[-10px] w-[5px] h-[5px] bg-brand-highlight rounded-[2.5px] dot" />
          )}
        </div>
        <Tooltip
          openDelay={1000}
          label={
            <div className="p-[6px]">
              <div>
                {notification.createdAt
                  ? getRelativeDateString(new Date(notification.createdAt))
                  : ''}
              </div>
            </div>
          }
        >
          <div className="space-y-[2px]">
            {multiLineContentNode}
            {notification.createdAt && size === 'large' ? (
              <div className="typography-action-sm text-[11px] opacity-40">
                {getRelativeDateString(new Date(notification.createdAt))}
              </div>
            ) : (
              ''
            )}
          </div>
        </Tooltip>
        <div className="flex relative">
          {notification.body || notification.subtitle ? (
            <div className="mt-1 overflow-hidden mr-[32px] whitespace-normal line-clamp-2 text-ellipsis typography-action-sm font-medium text-[12px]">
              {notification.body || notification.subtitle}
            </div>
          ) : null}
        </div>
      </div>
    </WrapperElement>
  );

  const finalFeedItemNode = isInTicker ? singleLineLayout : multiLineLayout;

  return markSeen ? (
    <InView
      threshold={1}
      onChange={(inView) => {
        if (inView) {
          if ('seen' in notification && !notification.seen) {
            markAsSeen();
          }
        }
      }}
    >
      {finalFeedItemNode}
    </InView>
  ) : (
    finalFeedItemNode
  );
}